import React, {useEffect} from 'react';
import {IGlobalStorefrontSettings, IStorefrontSettings, PAYMENT_PROCESSOR_KEYS} from '../../models/Storefront';
import {IDropdownOption} from '@fluentui/react';

import NumberField from '../Inputs/NumberField';
import CurrencySelection from '../CurrencySelection/CurrencySelection';
import PaymentProcessorDropdown from './PaymentProcessorDropdown';
import {Dropdown} from "@fluentui/react/lib/Dropdown";
import {
    useGetTaxCodesQuery
} from "../../store/Api";
import {
    useGetOrganizationFeatureSettingsQuery,
} from "../../store/endpoints/OrganizationsEndpoints"

import ShippingChargeSelection from './ShippingChargeSelection';
import CountrySelection from '../CountrySelection/CountrySelection';
import ToggleField from '../Common/ToggleField';
import {getCurrencySymbol} from '../../logic/currencyLocalizationHelper';
import SubExtensionDropdown from "../ExtensionFolios/SubExtensionDropdown";
import {ExtensionType} from "../../Constants";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";


interface IStorefrontSettingsFormProps {
    globalSettings?: IGlobalStorefrontSettings;
    settings: IStorefrontSettings;
    onChange: (settings: IStorefrontSettings) => void;
    onLoading?: (isLoading: boolean) => void;
}

const StorefrontPaymentShippingSettingsForm: React.FC<IStorefrontSettingsFormProps> = (props) => {
    const org = useSelector((state: RootState) => state.settings.selectedOrganization);

    const orgFeatures = useGetOrganizationFeatureSettingsQuery(org ?? '', {skip: !org || org === ''})
    const taxCodes = useGetTaxCodesQuery();
    const [taxCodeOptions, setTaxCodeOptions] = React.useState<IDropdownOption[]>([]);

    useEffect(() => {
        if(taxCodes.data){
            let unsetOption = {key: '', text: 'Not Set'};
            if(props.globalSettings?.defaultTaxCode){
                const related = taxCodes?.data?.find(t => t.name === props.globalSettings?.defaultTaxCode);
                if(related){
                    unsetOption = {key: related.name ?? '', text: `Inherited From Global: ${related.name} (${related.rate?.toString()}%)`};
                }
            }

            let options = [unsetOption, {key: 'NO_TAX', text: 'No Tax (0%)'}];

            const savedOptions = taxCodes.data.map(taxCode => (
                {
                    key: taxCode.name ?? '',
                    text: `${taxCode.name ?? ''} (${taxCode.rate?.toString()}%)`
                }
            ));
            options = [...options, ...savedOptions];
            setTaxCodeOptions(options);
        }
    }, [taxCodes.data, props?.globalSettings?.defaultTaxCode]);


    const canSetTaxCode = () => {
        const taxIntegrationSet = (props.settings.taxExtension !== undefined && props.settings.taxExtension !== '' && props.settings.taxExtension !== 'DISABLED');
        return (props?.globalSettings?.collectTax && orgFeatures?.data?.enableSimpleTaxCalculation && !taxIntegrationSet)
    }

    return (
        <div>
            <h3>Shipping Settings</h3>
            <CountrySelection value={props.settings.defaultCountry} onChange={(country) => props.onChange({...props.settings, defaultCountry: country})} />
            <ToggleField inline={true} label='Allow Previous Addresses' checked={props.settings.allowPreviousAddresses} onChange={(_e, c) => props.onChange({...props.settings, allowPreviousAddresses: c})} />
            <ShippingChargeSelection value={props.settings.shippingChargeType} onChange={(v) => props.onChange({...props.settings, shippingChargeType: v ?? ''})} label='Shipping Charge Type' />
            <NumberField prefix={getCurrencySymbol(props.settings.currency ?? props.globalSettings?.currency ?? 'USD')} label='Shipping Charge Amount' decimal={true} value={props.settings.shippingCharge} onChange={(v) => props.onChange({...props.settings, shippingCharge: v ?? undefined})} placeholder={props.globalSettings?.defaultShippingCharge?.toFixed(2) ?? 'Not Set'} />
            <h3>Payment Settings</h3>
            <CurrencySelection value={props.settings.currency} onChange={(v) => props.onChange({...props.settings, currency: v ?? ''})} allowInherit />
            <PaymentProcessorDropdown label='Payment Processor' value={props.settings.paymentProcessor ?? PAYMENT_PROCESSOR_KEYS.NONE} onChange={(v) => props.onChange({...props.settings, paymentProcessor: v ?? ''})} />
            <h3>Tax Settings</h3>
            <SubExtensionDropdown
                extension={props.settings.taxExtension}
                type={ExtensionType.Tax}
                onChange={(s) => {props.onChange({...props.settings, taxExtension: s})}}
                label='Tax Extension'
            />
            {canSetTaxCode() &&
                <Dropdown
                    label={'Tax Code'}
                    placeholder={'Not Set'}
                    selectedKey={props.settings.taxCode}
                    options={taxCodeOptions}
                    onChange={(_,d) => {
                        if (d) {
                            props.onChange({...props.settings, taxCode: d.key.toString()});
                        }
                    }}
                />}
            <h3>Checkout Location Lookup</h3>
            <SubExtensionDropdown
                extension={props.settings.locationLookupExtension}
                label={'Location Lookup Extension'}
                type={ExtensionType.Locations}
                onChange={(s) => {props.onChange({...props.settings, locationLookupExtension: s})}}
            />
        </div>
    );
}

export default StorefrontPaymentShippingSettingsForm;